import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { getMyBugs, getBugComments, addBugComment } from '../utils/api';
import { FaBug, FaHourglassHalf, FaCheckCircle, FaComment, FaPaperclip, FaEye, FaEyeSlash, FaSpinner, FaSearch, FaUser, FaCalendar, FaEdit, FaTrash, FaPlus, FaList, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import ImageViewer from '../components/ImageViewer';
import AttachmentPreview from '../components/AttachmentPreview';
import './ITSupport.css';

const STATUS_COLORS = {
  'New': '#ff6b6b',
  'Assigned': '#ffb74d',
  'In Progress (Accepted)': '#4ecdc4',
  'Fixed': '#81c784',
  'Fixed (Verified)': '#4caf50',
  'Won\'t fix (Infeasible)': '#9e9e9e',
  'Won\'t fix (Not reproducible)': '#9e9e9e',
  'Won\'t fix (Obsolete)': '#9e9e9e',
  'Won\'t fix (Intended behavior)': '#9e9e9e',
  'Inactive': '#9e9e9e',
  'Duplicate': '#9e9e9e'
};

const MODULE_OPTIONS = [
  'Dashboard', 'Organization Structure', 'Workflows', 'Company OKRs', 'Personal OKRs',
  'Team OKRs', 'My OKRs', 'Annual OKRs', 'People', 'Teams', 'Departments', 'Visual Map',
  'Safety Procedures', 'SLAs', 'Chat', 'Cost Cards', 'Template Picker', 'Create Cost Card',
  'Edit Cost Card', 'Create Custom Template', 'Edit Custom Template', 'Create OKR',
  'Edit OKR', 'Technical Sales Workflow', 'Store Management Workflow', 'Sales Workflow',
  'Import Workflow', 'Job Descriptions', 'Employees Reviews', 'Interview Processes',
  'Company Strategy', 'Business Continuity Plan', 'Data Management SOP', 'Procurement SOP',
  'Risk Compliance SOP', 'Settings', 'Search Results', 'Other',
];

const PRIORITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];

function StatusBadge({ status }) {
  return (
    <span
      className="bug-status-badge"
      style={{ background: STATUS_COLORS[status] || '#bdbdbd' }}
    >
      {status === 'New' && <FaBug style={{ marginRight: 4 }} />}
      {status === 'Assigned' && <FaHourglassHalf style={{ marginRight: 4 }} />}
      {status === 'In Progress (Accepted)' && <FaHourglassHalf style={{ marginRight: 4 }} />}
      {status === 'Fixed' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status === 'Fixed (Verified)' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status.startsWith('Won\'t fix') && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status === 'Inactive' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status === 'Duplicate' && <FaCheckCircle style={{ marginRight: 4 }} />}
      {status}
    </span>
  );
}

function PriorityBadge({ priority }) {
  return <span className={`bug-priority ${priority.toLowerCase()}`}>{priority}</span>;
}

// Comment component for displaying individual comments
function Comment({ comment, onEdit, onDelete, onSave, onCancel, currentUser, isEditing, bugId }) {
  const [editContent, setEditContent] = useState(comment.content);

  const isAuthor = comment.authorEmail === currentUser?.email;
  const isAdmin = currentUser?.role === 'super';

  const handleSave = () => {
    onSave(bugId, comment._id, editContent);
  };

  const handleCancel = () => {
    setEditContent(comment.content);
    onCancel();
  };

  if (isEditing) {
    return (
      <div style={{ 
        background: 'white', 
        padding: '12px', 
        borderRadius: '6px', 
        marginBottom: '8px',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontWeight: '600', color: '#004080' }}>{comment.author}</span>
          <span style={{ fontSize: '12px', color: '#666' }}>
            {new Date(comment.timestamp).toLocaleString()}
          </span>
        </div>
        <textarea
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          style={{ 
            width: '100%', 
            padding: '8px', 
            borderRadius: '4px', 
            border: '1px solid #ddd',
            marginBottom: '8px',
            minHeight: '60px',
            resize: 'vertical'
          }}
        />
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleCancel}
            style={{
              padding: '6px 12px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '6px 12px',
              background: '#004080',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      background: '#f8f9fa', 
      padding: '12px', 
      borderRadius: '6px', 
      marginBottom: '8px',
      border: '1px solid #e9ecef'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontWeight: '600', color: '#004080' }}>{comment.author}</span>
        <span style={{ fontSize: '12px', color: '#666' }}>
          {new Date(comment.timestamp).toLocaleString()}
        </span>
      </div>
      <div style={{ marginBottom: '8px', lineHeight: '1.4' }}>{comment.content}</div>
      
      {/* Display attachments if any */}
      {comment.attachments && comment.attachments.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <h5 style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Attachments:</h5>
          {comment.attachments.map((attachment, index) => (
            <AttachmentPreview key={index} attachment={attachment} />
          ))}
        </div>
      )}
      
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        {isAuthor && (
          <button
            onClick={() => onEdit(comment._id)}
            style={{
              padding: '4px 8px',
              background: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            <FaEdit style={{ marginRight: '4px' }} />
            Edit
          </button>
        )}
        {(isAuthor || isAdmin) && (
          <button
            onClick={() => onDelete(bugId, comment._id)}
            style={{
              padding: '4px 8px',
              background: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            <FaTrash style={{ marginRight: '4px' }} />
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

export default function ITSupport() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('new');
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [commentForm, setCommentForm] = useState({});
  const [commentAttachments, setCommentAttachments] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const [editingComment, setEditingComment] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    loadBugs();
  }, []);

  async function loadBugs() {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading bugs for user:', user?.email);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setBugs([]);
        return;
      }
      
      const bugsData = await getMyBugs(token);
      console.log('Bugs loaded:', bugsData);
      setBugs(bugsData || []);
      
      // Comments will be loaded on-demand when expanding a bug
      setComments({});
    } catch (err) {
      console.error('Failed to load bugs:', err);
      setError('Failed to load support tickets. Please try again.');
      setBugs([]);
    } finally {
      setLoading(false);
    }
  }

  async function toggleExpand(bugId) {
    const isExpanding = !expandedRows[bugId];
    setExpandedRows(prev => ({ ...prev, [bugId]: !prev[bugId] }));
    
    // Load comments only when expanding and if not already loaded
    if (isExpanding && !comments[bugId]) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const commentsData = await getBugComments(bugId, token);
          setComments(prev => ({ ...prev, [bugId]: commentsData || [] }));
        }
      } catch (err) {
        console.error('Failed to load comments for bug:', bugId, err);
        setComments(prev => ({ ...prev, [bugId]: [] }));
      }
    }
  }

  function handleCommentChange(bugId, content) {
    setCommentForm(prev => ({ ...prev, [bugId]: content }));
  }

  function handleCommentAttachmentChange(bugId, files) {
    setCommentAttachments(prev => ({ ...prev, [bugId]: Array.from(files) }));
  }

  function removeCommentAttachment(bugId, index) {
    setCommentAttachments(prev => {
      const newAttachments = [...(prev[bugId] || [])];
      newAttachments.splice(index, 1);
      return { ...prev, [bugId]: newAttachments };
    });
  }

  async function submitComment(bugId) {
    if (!commentForm[bugId]?.trim()) return;
    
    try {
      setSubmittingComment(prev => ({ ...prev, [bugId]: true }));
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Authentication token not found. Please log in again.');
        return;
      }
      
      // Prepare comment data with attachments
      const commentData = {
        content: commentForm[bugId],
        attachments: commentAttachments[bugId] || []
      };
      
      await addBugComment(bugId, commentData, token);
      setCommentForm(prev => ({ ...prev, [bugId]: '' }));
      setCommentAttachments(prev => ({ ...prev, [bugId]: [] }));
      await loadBugs(); // Reload to get updated comments
    } catch (err) {
      console.error('Failed to submit comment:', err);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setSubmittingComment(prev => ({ ...prev, [bugId]: false }));
    }
  }

  function handleEditComment(commentId) {
    setEditingComment(commentId);
  }

  function handleCancelEdit() {
    setEditingComment(null);
  }

  async function handleSaveComment(bugId, commentId, content) {
    try {
      // Implement comment editing logic here
      setEditingComment(null);
      await loadBugs(); // Reload to get updated comments
    } catch (err) {
      console.error('Failed to save comment:', err);
    }
  }

  async function handleDeleteComment(bugId, commentId) {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      // Implement comment deletion logic here
      await loadBugs(); // Reload to get updated comments
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  }



  const filteredBugs = bugs.filter(bug => {
    if (activeTab === 'new') return bug.status === 'New';
    if (activeTab === 'in-progress') return bug.status === 'Assigned' || bug.status === 'In Progress (Accepted)';
    if (activeTab === 'resolved') return bug.status === 'Fixed' || bug.status === 'Fixed (Verified)';
    if (activeTab === 'all') return true;
    return false;
  });

  if (loading) {
    return (
      <div className="it-support-page">
        <div className="it-support-header">
          <h1>IT Support</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <FaSpinner className="spin" style={{ fontSize: '48px', color: '#004080' }} />
          <p style={{ marginTop: '16px', color: '#666' }}>Loading your support tickets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="it-support-page">
        <div className="it-support-header">
          <h1>IT Support</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <div style={{ color: '#dc3545', fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <p style={{ color: '#dc3545', marginBottom: '16px' }}>{error}</p>
          <button 
            onClick={loadBugs}
            style={{
              background: '#004080',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="it-support-page">
      <div className="it-support-header">
        <h1>IT Support</h1>
      </div>

      <div className="it-support-content">
        <div className="it-support-actions">
          <div className="tab-navigation">
            <button
              className={`tab-button ${activeTab === 'new' ? 'active' : ''}`}
              onClick={() => setActiveTab('new')}
            >
              New ({bugs.filter(b => b.status === 'New').length})
            </button>
            <button
              className={`tab-button ${activeTab === 'in-progress' ? 'active' : ''}`}
              onClick={() => setActiveTab('in-progress')}
            >
              In Progress ({bugs.filter(b => b.status === 'Assigned' || b.status === 'In Progress (Accepted)').length})
            </button>
            <button
              className={`tab-button ${activeTab === 'resolved' ? 'active' : ''}`}
              onClick={() => setActiveTab('resolved')}
            >
              Resolved ({bugs.filter(b => b.status === 'Fixed' || b.status === 'Fixed (Verified)').length})
            </button>
            <button
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All ({bugs.length})
            </button>
          </div>
          
          <button
            className="create-ticket-btn"
            onClick={() => navigate('/contact-it-support')}
            style={{
              background: '#004080',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#0056b3';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#004080';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            <FaPlus /> Create New Ticket
          </button>
        </div>

        {filteredBugs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#666' }}>
            <FaList style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
            <p>No {activeTab === 'all' ? '' : activeTab} support tickets found.</p>
            {bugs.length === 0 && (
              <p style={{ marginTop: '16px', fontSize: '14px' }}>
                You haven't submitted any support tickets yet.
              </p>
            )}
          </div>
        ) : (
          <div className="bugs-list">
            {filteredBugs.map(bug => (
              <div key={bug._id} className="bug-item">
                <div className="bug-header" onClick={() => toggleExpand(bug._id)}>
                  <div className="bug-info">
                    <div className="bug-title">
                      <strong>#{bug.ticketId || bug._id}</strong> - {bug.issueTitle}
                    </div>
                    <div className="bug-meta">
                      <span className="bug-module">{bug.module}</span>
                      <PriorityBadge priority={bug.priority} />
                      <StatusBadge status={bug.status} />
                    </div>
                  </div>
                  <div className="bug-actions">
                    <span className="bug-date">
                      {new Date(bug.timestamp).toLocaleDateString()}
                    </span>
                    <button className="expand-button">
                      {expandedRows[bug._id] ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {expandedRows[bug._id] && (
                  <div className="bug-details">
                    <div className="bug-description">
                      <h4>Description</h4>
                      <p>{bug.description}</p>
                      
                      {bug.errorMessage && (
                        <div style={{ marginTop: '16px' }}>
                          <h4>Error Message</h4>
                          <p style={{ color: '#dc3545', fontFamily: 'monospace' }}>{bug.errorMessage}</p>
                        </div>
                      )}
                      
                      {bug.expected && (
                        <div style={{ marginTop: '16px' }}>
                          <h4>Expected vs Actual</h4>
                          <p>{bug.expected}</p>
                        </div>
                      )}
                      
                      {bug.steps && bug.steps.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                          <h4>Steps to Reproduce</h4>
                          <ol>
                            {bug.steps.map((step, index) => (
                              <li key={index}>{step}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      
                      {bug.attachment && (
                        <div style={{ marginBottom: '16px' }}>
                          <h4>Attachment</h4>
                          <AttachmentPreview attachment={bug.attachment} />
                        </div>
                      )}
                    </div>
                     
                    {/* Comments Section */}
                    <div className="comments-section">
                      <h4>Comments ({comments[bug._id]?.length || 0})</h4>
                      
                      <div className="comments-list">
                        {comments[bug._id]?.map(comment => (
                          <Comment
                            key={comment._id}
                            comment={comment}
                            currentUser={user}
                            isEditing={editingComment === comment._id}
                            onEdit={handleEditComment}
                            onDelete={handleDeleteComment}
                            onSave={handleSaveComment}
                            onCancel={handleCancelEdit}
                            bugId={bug._id}
                          />
                        ))}
                        {(!comments[bug._id] || comments[bug._id].length === 0) && (
                          <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontStyle: 'italic' }}>
                            No comments yet. Be the first to add one!
                          </div>
                        )}
                      </div>
                      
                      {/* Comment Form for open tickets */}
                      {bug.status !== 'Fixed' && bug.status !== 'Fixed (Verified)' && (
                        <div className="comment-form">
                          <textarea
                            value={commentForm[bug._id] || ''}
                            onChange={(e) => handleCommentChange(bug._id, e.target.value)}
                            placeholder="Add additional information or ask a question..."
                            rows={3}
                            className="comment-textarea"
                          />
                          
                          {/* File Attachment Section */}
                          <div className="comment-attachments">
                            <label className="attachment-label">
                              <input
                                type="file"
                                multiple
                                accept="image/*,.log,.txt,.pdf"
                                onChange={(e) => handleCommentAttachmentChange(bug._id, e.target.files)}
                                style={{ display: 'none' }}
                              />
                              <span className="attachment-button">
                                <FaPaperclip /> Add Files
                              </span>
                            </label>
                            
                            {/* Display selected attachments */}
                            {commentAttachments[bug._id] && commentAttachments[bug._id].length > 0 && (
                              <div className="selected-attachments">
                                {commentAttachments[bug._id].map((file, index) => (
                                  <div key={index} className="selected-attachment">
                                    <span className="attachment-name">{file.name}</span>
                                    <button
                                      type="button"
                                      onClick={() => removeCommentAttachment(bug._id, index)}
                                      className="remove-attachment-btn"
                                    >
                                      <FaTimes />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="comment-form-actions">
                            <button
                              onClick={() => submitComment(bug._id)}
                              disabled={!commentForm[bug._id]?.trim() || submittingComment[bug._id]}
                              className="submit-comment-btn"
                            >
                              {submittingComment[bug._id] ? 'Adding...' : 'Add Comment'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <ImageViewer
          src={selectedImage.src}
          alt={selectedImage.alt}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
}
